/**
 * Second sign-in step: the pure rules the screens share (no React, no DOM, no network; tested by test/second-step.test.mjs).
 * What the server says (api/src/core/auth/mfa.js) is turned into what the person reads here, in one place:
 *   which screen a session owes (none, the code, or setting the step up), how a typed code is tidied, what a refusal
 *   means in plain words, how the setup key and the recovery codes are laid out, and which roles a store may name.
 * The server decides everything; this file only words it.
 */

/** The `mfa` object of /v1/app/me and GET /v1/app/auth/mfa. Absent when the second step is switched off on the server. */
export interface SecondStepStatus {
  enrolled: boolean;
  enrolled_at: string | null;
  required: boolean;
  pending: boolean;
  enrol_required: boolean;
  recovery_codes_left: number;
  deadline: string | null;
}

/** What a session still owes before it may see the app: nothing, a code, or setting the step up first. */
export type Step = 'none' | 'code' | 'enrol';

type Raw = Record<string, unknown>;
const isObj = (v: unknown): v is Raw => !!v && typeof v === 'object' && !Array.isArray(v);

/** The server's status object, or null when the answer carries none (the step is off for everyone). */
export function statusOf(raw: unknown): SecondStepStatus | null {
  if (!isObj(raw)) return null;
  return {
    enrolled: raw.enrolled === true,
    enrolled_at: typeof raw.enrolled_at === 'string' ? raw.enrolled_at : null,
    required: raw.required === true,
    pending: raw.pending === true,
    enrol_required: raw.enrol_required === true,
    recovery_codes_left: Number.isInteger(raw.recovery_codes_left) ? (raw.recovery_codes_left as number) : 0,
    deadline: typeof raw.deadline === 'string' ? raw.deadline : null,
  };
}

export function stepOf(s: Pick<SecondStepStatus, 'pending' | 'enrol_required'> | null | undefined): Step {
  if (!s?.pending) return 'none';
  return s.enrol_required ? 'enrol' : 'code';
}

/** The same question asked of a sign-in answer ({mfa_required, mfa_enrol_required}; both absent when nothing is owed). */
export function stepOfLogin(answer: unknown): Step {
  if (!isObj(answer) || answer.mfa_required !== true) return 'none';
  return answer.mfa_enrol_required === true ? 'enrol' : 'code';
}

/** A 401 that means the session is gone (sign in again), as opposed to a refused password or code (show the answer). */
const ENDED = ['unauthorized', 'session_expired', 'session_revoked'];
export const sessionEnded = (status: number, code: string) => status === 401 && ENDED.includes(code);

// ── what is typed ───────────────────────────────────────────────────────────────────────────────────────────────────
/** A six-digit code as typed or pasted ("123 456", "123-456"): digits only, six at most. */
export const cleanCode = (input: string) => input.replace(/\D/g, '').slice(0, 6);
export const codeReady = (code: string) => /^\d{6}$/.test(code);

/** A recovery code as typed or pasted: lower case, the letters and digits the codes use, shown as xxxx-xxxx-xxxx. */
export function cleanRecovery(input: string) {
  const s = input
    .toLowerCase()
    .replace(/[^a-z2-7]/g, '')
    .slice(0, 12);
  return [s.slice(0, 4), s.slice(4, 8), s.slice(8, 12)].filter(Boolean).join('-');
}
export const recoveryReady = (code: string) => /^[a-z2-7]{4}-[a-z2-7]{4}-[a-z2-7]{4}$/.test(code);

// ── refusals ────────────────────────────────────────────────────────────────────────────────────────────────────────
/** "15 minutes", "1 minute": a wait rounded up to whole minutes. */
export function waitText(seconds: number) {
  const m = Math.max(1, Math.ceil(seconds / 60));
  return m === 1 ? '1 minute' : `${m} minutes`;
}

/**
 * What a refusal asks of the screen:
 *   wrong     the code did not match: say so, keep the form
 *   locked    too many tries: say how long to wait
 *   password  the password given for setup did not match
 *   reauth    setup needs the password first (the session is not fresh)
 *   restart   the setup timed out: start it again
 *   state     the account changed underneath (set up elsewhere, or reset): read the status again
 *   signin    the session is gone: back to sign-in
 *   policy    the store's rule refuses it
 *   other     anything else: show the text
 */
export type RefusalKind = 'wrong' | 'locked' | 'password' | 'reauth' | 'restart' | 'state' | 'signin' | 'policy' | 'other';
export interface Refusal {
  kind: RefusalKind;
  text: string;
}
export interface Failure {
  status: number;
  code: string;
  body?: unknown;
}

export const POLICY_TEXT = 'Your store requires the second sign-in step for your role, so it can’t be turned off here.';
export const ENROL_FIRST_TEXT = 'Set up your own second sign-in step first. That way you can still sign in once the store requires it.';

/** `entry` = what was typed: a six-digit code or a recovery code. */
export function refusal(f: Failure, entry: 'code' | 'recovery' = 'code'): Refusal {
  if (sessionEnded(f.status, f.code)) return { kind: 'signin', text: 'Your sign-in timed out. Sign in again.' };
  if (f.status === 429 || f.code === 'rate_limited') {
    const s = isObj(f.body) && typeof f.body.retry_after_s === 'number' && f.body.retry_after_s > 0 ? f.body.retry_after_s : null;
    return {
      kind: 'locked',
      text: s
        ? `Too many tries. To protect your account, codes are paused. Wait up to ${waitText(s)}, then try again.`
        : 'Too many tries. Wait a few minutes, then try again.',
    };
  }
  switch (f.code) {
    case 'invalid_code':
    case 'totp_invalid':
      return entry === 'recovery'
        ? { kind: 'wrong', text: 'That recovery code isn’t valid. Each one works once. Check it and try another.' }
        : {
            kind: 'wrong',
            text: 'That code didn’t match. Codes change every 30 seconds and each works once. Enter the one showing now.',
          };
    case 'invalid_request':
      return {
        kind: 'wrong',
        text: entry === 'recovery' ? 'Enter a recovery code, like abcd-efgh-2345.' : 'Enter the six digits from your app.',
      };
    case 'invalid_credentials':
      return { kind: 'password', text: 'That password doesn’t match.' };
    case 'reauth_required':
      return { kind: 'reauth', text: 'Enter your password to continue.' };
    case 'enrolment_expired':
    case 'enrolment_not_found':
      return { kind: 'restart', text: 'That setup timed out. Start again and scan the new code.' };
    case 'mfa_already_enrolled':
      return { kind: 'state', text: 'The second sign-in step is already set up on this account.' };
    case 'mfa_not_enrolled':
      return { kind: 'state', text: 'The second sign-in step isn’t set up on this account.' };
    case 'mfa_required':
      return { kind: 'state', text: 'Enter a code to continue.' };
    case 'mfa_required_by_policy':
      return { kind: 'policy', text: POLICY_TEXT };
    case 'mfa_enrol_first':
      return { kind: 'policy', text: ENROL_FIRST_TEXT };
    case 'owner_required':
      return { kind: 'policy', text: 'Only the store’s owner can change who must use the second sign-in step.' };
    case 'unknown_role':
      return { kind: 'other', text: 'One of those roles no longer exists. Reload and choose again.' };
    case 'feature_disabled':
    case 'not_found':
      return { kind: 'other', text: 'The second sign-in step isn’t available on this account yet.' };
    case 'network':
      return { kind: 'other', text: 'Couldn’t reach the server. Check your connection and try again.' };
    default:
      return { kind: 'other', text: f.status >= 500 ? 'Something went wrong on our side. Try again.' : 'That didn’t work. Try again.' };
  }
}

// ── what is shown ───────────────────────────────────────────────────────────────────────────────────────────────────
/** The setup key in groups of four, as authenticator apps show it: easier to read aloud and to type. */
export const groupSecret = (secret: string) => (secret.replace(/\s+/g, '').match(/.{1,4}/g) ?? []).join(' ');

/** The text file a person saves: what the codes are for, whose they are, when they were made, one code per line. */
export function recoveryFile(o: { codes: string[]; account: string; product: string; madeAt: Date }) {
  return [
    `${o.product}: recovery codes`,
    '',
    `Account: ${o.account || 'your account'}`,
    `Made: ${o.madeAt.toISOString().slice(0, 10)}`,
    '',
    'Each code signs you in once if you can’t use your authenticator app.',
    'Keep them somewhere safe and private. Making new codes cancels these.',
    '',
    ...o.codes,
    '',
  ].join('\n');
}

/** "October 12, 2026" in the store's time zone. Empty when there is no date. */
export function onSince(iso: string | null, timeZone: string) {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '';
  return new Intl.DateTimeFormat('en-US', { timeZone, day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(t));
}

/** How many recovery codes are left, in words. */
export function codesLeftText(n: number) {
  if (n <= 0) return 'No recovery codes left. Make new ones now.';
  if (n === 1) return '1 recovery code left. Make new ones soon.';
  return n <= 3 ? `${n} recovery codes left. Make new ones soon.` : `${n} recovery codes left.`;
}

// ── the store's rule ────────────────────────────────────────────────────────────────────────────────────────────────
export interface RoleChoice {
  key: string;
  name: string;
}
/** The roles a store may ask the step of: every role but technicians, who sign in to the technician app with a text code. */
export const policyRoles = <T extends RoleChoice>(roles: T[]): T[] => roles.filter((r) => r.key !== 'technician');

/** The roles to send: the ticked ones that still exist, in the order the store lists its roles. */
export const policyPatch = (roles: RoleChoice[], ticked: Iterable<string>) => {
  const on = new Set(ticked);
  return policyRoles(roles)
    .map((r) => r.key)
    .filter((k) => on.has(k));
};

export const samePolicy = (a: Iterable<string>, b: Iterable<string>) => {
  const x = [...new Set(a)].sort();
  const y = [...new Set(b)].sort();
  return x.length === y.length && x.every((k, i) => k === y[i]);
};
